import SEO, { SITE_URL } from "@/components/base/SEO";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Hero from "./components/Hero";
import CategoryGrid from "./components/CategoryGrid";
import ArticleHub from "./components/ArticleHub";
import CTASection from "./components/CTASection";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "url": SITE_URL,
      "name": "Sugargoo Spreadsheet Directory",
      "description": "The ultimate Sugargoo Spreadsheet directory featuring the best replica finds, shopping guides, safety tips, and category browsing. Discover curated spreadsheet links for sneakers, clothing, bags, and accessories.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${SITE_URL}/?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": "Sugargoo Spreadsheet Directory",
      "url": SITE_URL,
      "description": "Curated directory of Sugargoo spreadsheet finds across sneakers, clothing, bags, and accessories. Helping shoppers discover the best replica spreadsheet links since 2024.",
      "sameAs": [
        "https://kaspreadsheet.com/"
      ]
    },
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/#webpage`,
      "url": SITE_URL,
      "name": "Sugargoo Spreadsheet Directory - Best Finds & Guides 2026",
      "description": "The ultimate Sugargoo Spreadsheet directory featuring the best replica finds, shopping guides, safety tips, and category browsing. Discover curated spreadsheet links for sneakers, clothing, bags, and accessories. Updated 2026.",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "about": { "@id": `${SITE_URL}/#organization` },
      "datePublished": "2026-06-15T00:00:00+00:00",
      "dateModified": "2026-06-17T00:00:00+00:00",
      "lastReviewed": "2026-06-17T00:00:00+00:00"
    }
  ]
};

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title="Sugargoo Spreadsheet Directory - Best Finds & Guides 2026"
        description="The ultimate Sugargoo Spreadsheet directory featuring the best replica finds, shopping guides, safety tips, and category browsing. Discover curated spreadsheet links for sneakers, clothing, bags, and accessories. Updated 2026."
        keywords="sugargoo spreadsheet, sugargoo finds, replica spreadsheet, sugargoo guide, spreadsheet directory, best replica finds"
        canonicalPath="/"
        ogType="website"
        jsonLd={homeJsonLd}
      />
      <Header />
      <main>
        <Hero />
        <CategoryGrid />
        <ArticleHub />
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-5">
                <p className="text-xs md:text-sm uppercase tracking-widest text-foreground-500 mb-3">
                  2026 Updated Guide
                </p>
                <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-5">
                  The Ultimate{" "}
                  <span className="italic font-light text-primary-500">Sugargoo</span>
                  <br />
                  Spreadsheet Directory
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-7 max-w-md">
                  We built this directory because finding quality Sugargoo spreadsheet links is frustrating. Our team personally vets every category, ensuring you get access to the most up-to-date, reliable finds across sneakers, clothing, bags, and accessories. No dead links. No outdated listings. Just pure spreadsheet value.
                </p>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Open Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
              <div className="lg:col-span-7">
                <div className="relative rounded-2xl md:rounded-3xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20fashion%20items%20including%20white%20sneakers%20denim%20jacket%20sunglasses%20and%20accessories%20artfully%20arranged%20on%20light%20gray%20textured%20surface%2C%20soft%20natural%20morning%20light%20streaming%20from%20window%2C%20editorial%20lifestyle%20photography%2C%20clean%20organized%20composition%2C%20modern%20casual%20aesthetic%2C%20peaceful%20productive%20mood%2C%20no%20text%20or%20logos&width=900&height=675&seq=sugargoo-about-v2&orientation=landscape"
                    alt="Sugargoo Spreadsheet directory overview with curated fashion finds"
                    title="The Ultimate Sugargoo Spreadsheet Directory"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-200/70">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="mb-12">
              <p className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                Verified Reviews
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight">
                Real Buyers{" "}
                <span className="italic font-light text-accent-600">Real Spreadsheet Finds</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: "Marcus T.",
                  handle: "@marcusfinder",
                  review: "I have been using Sugargoo spreadsheets for over a year now and this directory is by far the most organized one I have found. The sneaker category alone saved me hours of searching. Everything is neatly categorized and the links are always fresh. Highly recommend for anyone serious about finding quality pieces without the endless scrolling.",
                  seq: "review-marcus",
                },
                {
                  name: "Emily R.",
                  handle: "@emilyfinds",
                  review: "As someone new to the spreadsheet world, I was completely overwhelmed until I stumbled upon this directory. The beginner guide article explained everything clearly, and now I navigate spreadsheets like a pro. The bags and accessories categories are my absolute favorites — I have found some incredible pieces through these links.",
                  seq: "review-emily",
                },
                {
                  name: "David K.",
                  handle: "@davidspreadsheet",
                  review: "I have tried multiple spreadsheet directories before, but this one stands out for its consistent updates and honest curation. The comparison article between Sugargoo and Pandabuy helped me decide which platform works best for my needs. The safety guide also gave me peace of mind before placing my first order. Genuinely useful resource.",
                  seq: "review-david",
                },
              ].map((review) => (
                <div key={review.seq} className="bg-background-50 rounded-xl md:rounded-2xl p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-secondary-200 flex items-center justify-center shrink-0">
                      <span className="font-heading text-foreground-700 font-semibold text-sm">
                        {review.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.handle}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    &ldquo;{review.review}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
}